import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ticketAPI, metadataAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PlusCircle, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { getStatusBadgeClass, getSeverityPillClass } from '../utils/helpers';

export default function TicketListPage() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'closed' | 'all'
  const [statuses, setStatuses] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1');
  const navigate = useNavigate();
  const { isClient } = useAuth();

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (viewMode === 'closed') params.status = 'CLOSED';
      else if (viewMode === 'active' && !statusFilter) {
        params.exclude_status = 'CLOSED';
      }
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const resp = await ticketAPI.list(params);
      setTickets(resp.data.items);
      setTotal(resp.data.total);
      setTotalPages(resp.data.total_pages);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search, viewMode]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  useEffect(() => {
    metadataAPI.getStatuses().then(r => setStatuses(r.data)).catch(() => {});
  }, []);

  const handlePageChange = (newPage) => {
    setSearchParams({ page: String(newPage) });
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Tickets</h1>
          <p>{total} total tickets</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/tickets/new')}>
          <PlusCircle size={16} /> Raise ticket
        </button>
      </div>

      {/* View mode tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '2px solid var(--color-border)', paddingBottom: 0 }}>
        {[
          { id: 'active', label: 'Active' },
          { id: 'closed', label: 'Closed' },
          { id: 'all', label: 'All' },
        ].map(tab => (
          <button key={tab.id} onClick={() => { setViewMode(tab.id); setStatusFilter(''); setSearchParams({ page: '1' }); }}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer',
              color: viewMode === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
              borderBottom: viewMode === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: -2,
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--color-text-muted)' }} />
            <input
              className="form-input"
              style={{ paddingLeft: 32 }}
              placeholder="Search by ID, feature, module..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchTickets()}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Filter size={14} style={{ color: 'var(--color-text-muted)' }} />
            <select
              className="form-select"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {statuses.map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-sm" onClick={fetchTickets}>Apply</button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Issue ID</th>
                {!isClient && <th>Client</th>}
                <th>Type</th>
                <th>Feature</th>
                <th>Impact</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isClient ? 7 : 8} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>Loading...</td></tr>
              ) : tickets.length === 0 ? (
                <tr><td colSpan={isClient ? 7 : 8} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>No tickets found</td></tr>
              ) : tickets.map(t => (
                <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}>
                  <td style={{ fontWeight: 600, color: 'var(--color-primary)' }}>{t.issue_id}</td>
                  {!isClient && <td>{t.client_code}</td>}
                  <td>{t.issue_type}</td>
                  <td>{t.feature || '-'}</td>
                  <td><span className={`badge ${getSeverityPillClass(t.impact)}`}>{t.impact}</span></td>
                  <td><span className={`badge ${getSeverityPillClass(t.severity)}`}>{t.severity}</span></td>
                  <td><span className={`badge ${getStatusBadgeClass(t.status_display)}`}>{t.status_display}</span></td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--color-border)' }}>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              Page {page} of {totalPages} ({total} items)
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                <ChevronLeft size={14} /> Prev
              </button>
              <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
